import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { partnerLogos, associateLogos, associateHighlights, prideStats } from '../mock/mock';
import { Handshake, Globe, Award, Sparkles } from 'lucide-react';
import { Counter } from './StatsBanner';

const iconMap = { Handshake, Globe, Award, Sparkles };

const LogoCard = ({ item }) => {
  const [failed, setFailed] = useState(!item.src);
  return (
    <div className="shrink-0 mx-3 logo-card w-40" title={item.name}>
      {!failed && item.src ? (
        <img src={item.src} alt={item.name} loading="lazy" onError={() => setFailed(true)} />
      ) : (
        <span className="font-extrabold text-slate-800 text-base tracking-tight text-center px-2" style={{ color: item.color || '#0f172a' }}>
          {item.short || item.name}
        </span>
      )}
    </div>
  );
};

const PartnersSection = () => {
  return (
    <section id="partners" className="section-padding section-warm relative overflow-hidden">
      <div className="container-x relative">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="eyebrow-light mb-4"><span className="w-1.5 h-1.5 rounded-full bg-sky-500" /> Our Technology Partners</div>
          <h2 className="section-title-dark">Trusted <span className="gradient-text">Technology Partners</span></h2>
          <p className="section-subtitle-dark mx-auto mt-4">We partner with the world's leading cloud &amp; datacenter technology brands — AWS, Microsoft Azure, Google Cloud, VMware, Cisco, Dell, Fortinet, Veeam, Acronis, Fujitsu, TrueConf and more.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {prideStats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="p-5 rounded-2xl glass-light-card text-center transition-all"
            >
              <div className="text-3xl md:text-4xl font-extrabold gradient-text">
                <Counter to={s.value} suffix={s.suffix} />
              </div>
              <div className="mt-1 text-xs text-slate-500 uppercase tracking-widest font-semibold">{s.label}</div>
            </motion.div>
          ))}
        </div>

        <div className="relative overflow-hidden py-6">
          <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-amber-50/60 to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-amber-50/60 to-transparent z-10 pointer-events-none" />
          <div className="flex marquee-track">
            {[...partnerLogos, ...partnerLogos].map((p, i) => (<LogoCard key={i} item={p} />))}
          </div>
        </div>
        <div className="relative overflow-hidden py-6">
          <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-amber-50/60 to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-amber-50/60 to-transparent z-10 pointer-events-none" />
          <div className="flex marquee-track" style={{ animationDuration: '45s', animationDirection: 'reverse' }}>
            {[...associateLogos, ...associateLogos].map((p, i) => (<LogoCard key={`a${i}`} item={p} />))}
          </div>
        </div>

        <div className="text-center max-w-2xl mx-auto mt-20 mb-10">
          <div className="eyebrow-light mb-4"><span className="w-1.5 h-1.5 rounded-full bg-sky-500" /> Our Associates</div>
          <h2 className="section-title-dark">Collaborating with <span className="gradient-text">the Best</span></h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {associateHighlights.map((h, i) => {
            const Icon = iconMap[h.icon] || Handshake;
            return (
              <motion.div
                key={h.title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="p-5 rounded-2xl glass-light-card transition-all group"
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg shadow-sky-500/25">
                  <Icon className="text-white" size={20} />
                </div>
                <div className="font-bold text-slate-900">{h.title}</div>
                <div className="text-sm text-slate-600 mt-1">{h.description}</div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PartnersSection;
